import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenantConfig } from "@/context/TenantConfigContext";
import { PageHeader, BucketBadge, StatusBadge, Empty, AiBadge } from "@/components/ui-bits";
import { fmtINR, fmtNum } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { RowQuickActions, CommTemplateDialog } from "@/components/QuickActions";
import { buildClonePrefill } from "@/components/QuickAddAccountDialog";
import { openRetainWhatsApp } from "@/lib/retainWhatsApp";
import { ActivityLoggerButton } from "@/components/ActivityLogger";
import { MagnifyingGlass, UploadSimple, Plus, FileArrowDown, UsersThree, X, BookmarkSimple, PlusCircle, PaperPlaneTilt, Copy, WhatsappLogo } from "@phosphor-icons/react";

// Shared Quick Add trigger (reuses the single global QuickAddAccountDialog mounted in Layout).
const openQuickAdd = (source = "accounts") =>
  window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source } }));


const BUCKETS = ["Current", "1-30", "31-60", "61-90", "90+", "Unknown"];
const CLASS_COLOR = {
  "Likely To Pay": "bg-emerald-100 text-emerald-800",
  "Settlement Candidate": "bg-blue-100 text-blue-800",
  "Skip Trace Required": "bg-amber-100 text-amber-800",
  "Legal Candidate": "bg-red-100 text-red-800",
};

function fmtCustom(val, type) {
  if (val === undefined || val === null || val === "") return "—";
  if (type === "currency") return fmtINR(Number(val) || 0);
  if (type === "number") return fmtNum(Number(val) || 0);
  if (type === "checkbox") return val === true || val === "true" || val === "yes" ? "Yes" : "No";
  return String(val);
}

export default function Accounts() {
  const { user } = useAuth();
  const { terms, config } = useTenantConfig();
  const navigate = useNavigate();

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "recoveros_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the template");
    }
  };
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("");
  const [status, setStatus] = useState("");
  const [cfFilters, setCfFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);
  const [templates, setTemplates] = useState([]);
  const [commCtx, setCommCtx] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [agents, setAgents] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const canManage = ["super_admin", "client_admin", "manager"].includes(user.role);
  const isAuditor = user.role === "auditor";

  const customFields = (config?.custom_fields || []);
  const visibleCustom = customFields.filter((c) => c.visible !== false);
  const filterableCustom = customFields.filter((c) => c.filterable && (c.type === "select" || c.type === "checkbox"));

  const loadViews = useCallback(() => api.get("/accounts/views").then((r) => setViews(r.data)).catch(() => {}), []);
  useEffect(() => { loadViews(); }, [loadViews]);

  const applyView = (v) => {
    if (!v) { setActiveView(""); setQ(""); setBucket(""); setStatus(""); setCfFilters({}); return; }
    setActiveView(v.view_id);
    const fl = v.filters || {};
    setQ(fl.q || ""); setBucket(fl.bucket || ""); setStatus(fl.status || ""); setCfFilters(fl.cf || {});
  };
  const saveView = async () => {
    const name = viewName.trim();
    if (!name) return toast.error("Enter a view name");
    try {
      const r = await api.post("/accounts/views", { name, filters: { q, bucket, status, cf: cfFilters } });
      toast.success("View saved");
      setSaveOpen(false); setViewName("");
      await loadViews();
      setActiveView(r.data.view_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save view"); }
  };
  const deleteView = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/accounts/views/${id}`);
      if (activeView === id) setActiveView("");
      await loadViews();
      toast.success("View deleted");
    } catch { toast.error("Failed to delete view"); }
  };

  useEffect(() => { api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (canManage) api.get("/agents").then((r) => setAgents(r.data)).catch(() => {}); }, [canManage]);

  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit };
    if (q) params.q = q;
    if (bucket) params.bucket = bucket;
    if (status) params.status = status;
    Object.entries(cfFilters).forEach(([k, v]) => { if (v) params[`cf_${k}`] = v; });
    const r = await api.get("/accounts", { params });
    setData(r.data);
    setLoading(false);
  }, [q, bucket, status, limit, cfFilters]);

  useEffect(() => { setLimit(50); clearSel(); }, [q, bucket, status, cfFilters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="accounts-page">
      <PageHeader title={terms.nav_accounts} subtitle={`${fmtNum(data.total)} ${terms.entity_plural.toLowerCase()} in portfolio`}>
        {canManage && (
          <Button className="rounded-sm gap-2 bg-[#0B3D91] hover:bg-[#0a3578]" onClick={() => openQuickAdd("accounts")} data-testid="add-account-button">
            <Plus size={16} weight="bold" /> Add Account
          </Button>
        )}
        {canManage && (
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => navigate("/import")} data-testid="bulk-upload-button">
            <UploadSimple size={16} weight="bold" /> Bulk Upload
          </Button>
        )}
        {canManage && (
          <Button variant="outline" className="rounded-sm gap-2" onClick={downloadTemplate} data-testid="download-template-button">
            <FileArrowDown size={16} weight="bold" /> Download Template
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="saved-views-bar">
        <span className="label-caps text-muted-foreground mr-1">Views</span>
        <button
          onClick={() => applyView(null)}
          data-testid="view-all"
          className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${!activeView ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
        >All</button>
        {views.map((v) => (
          <span
            key={v.view_id}
            className={`inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 text-xs font-semibold rounded-full border transition-colors ${activeView === v.view_id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
          >
            <button onClick={() => applyView(v)} data-testid={`view-${v.view_id}`}>{v.name}</button>
            <button onClick={(e) => deleteView(v.view_id, e)} data-testid={`delete-view-${v.view_id}`} className="opacity-60 hover:opacity-100"><X size={12} weight="bold" /></button>
          </span>
        ))}
        <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-7" onClick={() => setSaveOpen(true)} data-testid="save-view-button">
          <BookmarkSimple size={14} weight="bold" /> Save current view
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="account-search"
            placeholder={`Search by name, ${(terms?.identifier || "Loan ID").toLowerCase()} or mobile…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-sm"
          />
        </div>
        <Select value={bucket || "all"} onValueChange={(v) => setBucket(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="filter-bucket"><SelectValue placeholder="DPD Bucket" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Buckets</SelectItem>
            {BUCKETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36 rounded-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {["active", "ptp", "settled", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {filterableCustom.map((cf) => {
          const opts = cf.type === "checkbox" ? ["Yes", "No"] : (cf.options || []);
          return (
            <Select key={cf.key} value={cfFilters[cf.key] || "all"} onValueChange={(v) => setCfFilters((p) => ({ ...p, [cf.key]: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-40 rounded-sm" data-testid={`filter-cf-${cf.key}`}><SelectValue placeholder={cf.label} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All {cf.label}</SelectItem>
                {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          );
        })}
      </div>

      {canManage && selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 p-3 border border-primary bg-primary/5 rounded-sm" data-testid="bulk-action-bar">
          <div className="flex items-center gap-2 text-sm font-medium">
            <button onClick={clearSel} className="text-muted-foreground hover:text-foreground" data-testid="bulk-clear"><X size={16} weight="bold" /></button>
            {selected.size} account{selected.size > 1 ? "s" : ""} selected
          </div>
          <Button size="sm" className="rounded-sm gap-2" onClick={() => setBulkOpen(true)} data-testid="bulk-assign-open">
            <UsersThree size={16} weight="bold" /> Assign to Agents
          </Button>
        </div>
      )}

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              {canManage && (
                <th className="p-3 w-10">
                  <Checkbox
                    checked={data.items.length > 0 && data.items.every((a) => selected.has(a.account_id))}
                    onCheckedChange={(c) => setSelected((p) => {
                      const n = new Set(p);
                      data.items.forEach((a) => c ? n.add(a.account_id) : n.delete(a.account_id));
                      return n;
                    })}
                    data-testid="select-all-checkbox"
                  />
                </th>
              )}
              <th className="text-left p-3">Record ID</th>
              <th className="text-left p-3">{terms.identifier}</th>
              <th className="text-left p-3">{terms.entity}</th>
              <th className="text-right p-3">{terms.amount}</th>
              <th className="text-right p-3">{config?.label_overrides?.receivable_amount || "Receivable"}</th>
              <th className="text-center p-3">DPD</th>
              <th className="text-center p-3">Bucket</th>
              <th className="text-center p-3">AI</th>
              <th className="text-left p-3">Class</th>
              <th className="text-left p-3">Agent</th>
              <th className="text-center p-3">Status</th>
              {visibleCustom.map((cf) => (
                <th key={cf.key} className={`p-3 ${cf.type === "currency" || cf.type === "number" ? "text-right" : "text-left"}`} data-testid={`th-cf-${cf.key}`}>{cf.label}</th>
              ))}
              {!isAuditor && <th className="text-center p-3">Quick Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.items.map((a) => (
              <tr
                key={a.account_id}
                onClick={() => navigate(`/accounts/${a.account_id}`)}
                className={`border-b border-border last:border-0 hover:bg-secondary/60 cursor-pointer ${selected.has(a.account_id) ? "bg-primary/5" : ""}`}
                data-testid={`account-row-${a.loan_id}`}
              >
                {canManage && (
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(a.account_id)} onCheckedChange={() => toggleOne(a.account_id)} data-testid={`select-${a.loan_id}`} />
                  </td>
                )}
                <td className="p-3 mono text-xs text-muted-foreground" data-testid={`record-id-${a.loan_id}`}>{a.record_id || "—"}</td>
                <td className="p-3 mono font-medium text-primary">{a.loan_id}</td>
                <td className="p-3">
                  <div className="font-medium">{a.borrower_name}</div>
                  <div className="text-xs text-muted-foreground mono">{a.mobile}</div>
                </td>
                <td className="p-3 text-right mono font-semibold">{fmtINR(a.outstanding_amount)}</td>
                <td className="p-3 text-right mono">{fmtINR(a.receivable_amount > 0 ? a.receivable_amount : a.loan_amount)}</td>
                <td className="p-3 text-center mono">{a.dpd == null ? "—" : a.dpd}</td>
                <td className="p-3 text-center"><BucketBadge bucket={a.bucket} /></td>
                <td className="p-3 text-center"><AiBadge score={a.ai_score} band={a.ai_band} /></td>
                <td className="p-3">
                  {a.account_classification
                    ? <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm ${CLASS_COLOR[a.account_classification] || "bg-secondary"}`}>{a.account_classification}</span>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="p-3 text-muted-foreground">
                  {(a.assigned_agent_names && a.assigned_agent_names.length > 0)
                    ? <div className="flex flex-wrap gap-1">{a.assigned_agent_names.map((n, i) => <span key={i} className="text-[11px] font-medium px-1.5 py-0.5 rounded-sm bg-secondary text-foreground">{n}</span>)}</div>
                    : (a.assigned_agent_name || <span className="italic">Unassigned</span>)}
                </td>
                <td className="p-3 text-center"><StatusBadge status={a.status} /></td>
                {visibleCustom.map((cf) => (
                  <td key={cf.key} className={`p-3 ${cf.type === "currency" || cf.type === "number" ? "text-right mono" : "text-left"}`} data-testid={`td-cf-${cf.key}-${a.loan_id}`}>
                    {fmtCustom(a.custom_fields?.[cf.key], cf.type)}
                  </td>
                ))}
                {!isAuditor && (
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-0.5">
                      <RowQuickActions account={a} onPick={(acc, ch) => setCommCtx({ account: acc, channel: ch })} onLogged={load} />
                      <ActivityLoggerButton accountId={a.account_id} onSaved={load} variant="icon" testIdSuffix={a.loan_id} borrowerName={a.borrower_name} outstanding={a.outstanding_amount} />
                      {canManage && (
                        <button
                          title="Start a Follow-up"
                          onClick={() => navigate(`/connect?account=${a.account_id}&compose=1`)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-transparent text-[#0B3D91] hover:bg-[#0B3D91]/10 transition-colors"
                          data-testid={`start-followup-${a.loan_id}`}
                        >
                          <PaperPlaneTilt size={16} weight="bold" />
                        </button>
                      )}
                      {canManage && (
                        <button
                          title="Clone Account"
                          onClick={() => window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source: "clone_list", prefill: buildClonePrefill(a) } }))}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-transparent text-slate-600 hover:bg-secondary transition-colors"
                          data-testid={`clone-account-${a.loan_id}`}
                        >
                          <Copy size={16} weight="bold" />
                        </button>
                      )}
                      {canManage && (
                        <button
                          title="WhatsApp — Retain message"
                          onClick={() => openRetainWhatsApp(a, toast)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-transparent text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
                          data-testid={`whatsapp-retain-${a.loan_id}`}
                        >
                          <WhatsappLogo size={16} weight="fill" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && data.items.length === 0 && (
          (() => {
            const noFilters = !q && !bucket && !status && !Object.values(cfFilters).some(Boolean);
            if (noFilters && data.total === 0 && canManage) {
              return (
                <div className="py-12 px-6 text-center" data-testid="accounts-activation-empty">
                  <div className="h-12 w-12 rounded-full bg-[#0B3D91]/10 flex items-center justify-center mx-auto">
                    <PlusCircle size={26} weight="fill" className="text-[#0B3D91]" />
                  </div>
                  <div className="font-heading font-bold text-lg text-slate-900 mt-3">Add your first receivable</div>
                  <p className="text-sm text-muted-foreground mt-1">Start with one account or import your whole portfolio.</p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    <Button className="rounded-sm gap-2 bg-[#16A34A] hover:bg-[#138a3e]" onClick={() => openQuickAdd("accounts")} data-testid="empty-add-one-btn">
                      <PlusCircle size={16} weight="bold" /> Add one account
                    </Button>
                    <Button variant="outline" className="rounded-sm gap-2 border-2 border-[#0B3D91] text-[#0B3D91]" onClick={() => navigate("/import")} data-testid="empty-import-btn">
                      <UploadSimple size={16} weight="bold" /> Import Excel
                    </Button>
                  </div>
                </div>
              );
            }
            return <Empty text="No accounts match your filters" />;
          })()
        )}
      </div>

      {data.items.length < data.total && (
        <div className="flex items-center justify-center mt-4">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => setLimit((l) => l + 50)}
            disabled={loading}
            data-testid="load-more-accounts"
          >
            {loading ? "Loading…" : `Load more (${data.items.length} of ${fmtNum(data.total)})`}
          </Button>
        </div>
      )}
      {!isAuditor && (
        <CommTemplateDialog ctx={commCtx} templates={templates} onClose={() => setCommCtx(null)} onSent={load} />
      )}
      {canManage && bulkOpen && (
        <BulkAssignDialog
          agents={agents}
          count={selected.size}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); clearSel(); load(); }}
          accountIds={[...selected]}
        />
      )}
      <Dialog open={saveOpen} onOpenChange={(o) => { setSaveOpen(o); if (!o) setViewName(""); }}>
        <DialogContent className="rounded-sm" data-testid="save-view-dialog">
          <DialogHeader><DialogTitle>Save current view</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">View name</Label>
            <Input
              placeholder="e.g. Insurance claims pending"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveView()}
              className="rounded-sm"
              data-testid="view-name-input"
            />
            <p className="text-xs text-muted-foreground">Saves your current search, bucket, status and custom-field filters. Private to you.</p>
          </div>
          <DialogFooter>
            <Button onClick={saveView} className="rounded-sm" data-testid="confirm-save-view">Save View</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BulkAssignDialog({ agents, count, accountIds, onClose, onDone }) {
  const [picked, setPicked] = useState(new Set());
  const [mode, setMode] = useState("replace");
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/accounts/bulk-assign", { account_ids: accountIds, agent_ids: [...picked], mode });
      toast.success(`${r.data.updated} account(s) assigned`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign {count} account{count > 1 ? "s" : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Select Agents (one or many)</Label>
            <div className="border border-border rounded-sm divide-y divide-border max-h-56 overflow-y-auto mt-1">
              {agents.length === 0 && <div className="p-3 text-sm text-muted-foreground">No agents available</div>}
              {agents.map((ag) => (
                <label key={ag.user_id} className="flex items-center gap-3 p-2.5 hover:bg-secondary/50 cursor-pointer" data-testid={`bulk-agent-${ag.user_id}`}>
                  <Checkbox checked={picked.has(ag.user_id)} onCheckedChange={() => toggle(ag.user_id)} />
                  <span className="text-sm font-medium">{ag.name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{ag.email}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="rounded-sm" data-testid="bulk-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="replace">Replace existing agents</SelectItem>
                <SelectItem value="add">Add to existing agents</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">{mode === "replace" ? "Selected agents become the only assignees." : "Selected agents are added alongside current assignees."}</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="bulk-assign-submit">{saving ? "Assigning…" : `Assign ${count}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
